import { RichTextEditor, RichTextEditorStylesNames } from '@mantine/tiptap';
import { isEmpty } from '@medplum/core';
import { useEditor } from '@tiptap/react';
import { CSSProperties, useEffect, useMemo } from 'react';
import { IconBraces, IconCodeDots, IconSeparator } from '@tabler/icons-react';
import { MentionItem } from './mention';
import { createRichTextExtensions } from './richTextExtensions';

interface RichTextInputProps {
  value: object | string;
  onChange: (text: string, json: any) => void;
  textareaInputRef?: React.RefObject<HTMLDivElement>;
  disabled?: boolean;
  placeholder?: string;
  mentionItems?: MentionItem[];
  editorKey?: string; // Key to force re-render when patient changes
  contentMinHeight?: string;
  contentMaxHeight?: string;
  styles?: Partial<Record<RichTextEditorStylesNames, CSSProperties>>;
}

export function RichTextInput(props: RichTextInputProps) {
  const {
    value,
    textareaInputRef,
    onChange,
    disabled = false,
    placeholder,
    mentionItems,
    editorKey,
    contentMinHeight,
    contentMaxHeight,
  } = props;

  const extensions = useMemo(() => {
    return createRichTextExtensions({
      mentionItems: mentionItems ?? [],
      placeholder: placeholder ?? '',
    });
  }, [mentionItems, placeholder]);

  // Create a stable combined key for recreation: recreates when patient changes OR mentions load
  const editor = useEditor(
    {
      extensions,
      content: value,
      onUpdate: ({ editor }) => {
        const json = editor.getJSON();
        const text = editor.getText();
        onChange(text, json);
      },
      parseOptions: {
        preserveWhitespace: 'full',
      },
      editable: !disabled,
    },
    [editorKey]
  );

  useEffect(() => {
    if (!editor) return;
    // Controlled consumers feed onUpdate's JSON straight back as `value`.
    // Re-setting identical content re-parses the doc and throws the cursor
    // out of list items mid-typing (first keystroke in a bullet jumped to a
    // new line) — skip the echo and only apply real external changes.
    if (JSON.stringify(editor.getJSON()) === JSON.stringify(value)) {
      return;
    }
    if (isEmpty(value)) {
      editor.commands.clearContent();
    } else {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [value, editor]);

  // Update editor editable state when disabled prop changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [editor, disabled]);

  return (
    <RichTextEditor editor={editor} w="100%" styles={props.styles}>
      <RichTextEditor.Toolbar sticky stickyOffset="var(--docs-header-height)">
        {/* Text formatting */}
        <RichTextEditor.ControlsGroup>
          <RichTextEditor.Bold />
          <RichTextEditor.Italic />
          <RichTextEditor.Strikethrough />
        </RichTextEditor.ControlsGroup>

        {/* Headings */}
        <RichTextEditor.ControlsGroup>
          <RichTextEditor.H1 />
          <RichTextEditor.H2 />
          <RichTextEditor.H3 />
        </RichTextEditor.ControlsGroup>

        {/* Links */}
        <RichTextEditor.ControlsGroup>
          <RichTextEditor.Link />
          <RichTextEditor.Unlink />
        </RichTextEditor.ControlsGroup>

        {/* Lists */}
        <RichTextEditor.ControlsGroup>
          <RichTextEditor.BulletList />
          <RichTextEditor.OrderedList />
        </RichTextEditor.ControlsGroup>

        {/* Block elements */}
        <RichTextEditor.ControlsGroup>
          <RichTextEditor.Blockquote />
          <RichTextEditor.Hr icon={() => <IconSeparator size={16} />} />
        </RichTextEditor.ControlsGroup>

        {/* Code */}
        <RichTextEditor.ControlsGroup>
          <RichTextEditor.Code icon={() => <IconBraces size={16} />} />
          <RichTextEditor.CodeBlock icon={() => <IconCodeDots size={16} />} />
        </RichTextEditor.ControlsGroup>

        {/* Clear formatting */}
        <RichTextEditor.ControlsGroup>
          <RichTextEditor.ClearFormatting />
        </RichTextEditor.ControlsGroup>
      </RichTextEditor.Toolbar>

      <RichTextEditor.Content
        ref={textareaInputRef}
        style={{
          minHeight: contentMinHeight ?? undefined,
          maxHeight: contentMaxHeight ?? '160px',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      />
    </RichTextEditor>
  );
}
